import React, { useMemo, useState } from "react";
import { Loader2, Plus, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMarketing, Campaign } from "../../../context/MarketingContext";
import { Button } from "../../../components/ui/Button";
import { CampaignDetailsDrawer } from "../../../components/marketing/CampaignDetailsDrawer";
import { TemplateListSkeleton } from "../../../components/marketing/TemplateListSkeleton";
import { useToast } from "../../../hooks/useToast";
import { formatApiErrors, runCampaign as runCampaignApi } from "../../../services/marketingTemplatesApi";

const CampaignsListPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const {
    campaigns,
    campaignsLoading,
    removeCampaign,
    customers,
    emailTemplates,
    smsTemplates,
  } = useMarketing();

  const [selected, setSelected] = useState<Campaign | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [runningCampaignId, setRunningCampaignId] = useState<string | null>(null);

  const enrichedCampaigns = useMemo(
    () =>
      campaigns.map((c) => ({
        ...c,
        audienceLabel: c.useAllCustomers
          ? `All customers (${customers.length})`
          : `${c.selectedCustomerIds.length} selected customer${c.selectedCustomerIds.length === 1 ? "" : "s"}`,
        emailTemplateName:
          emailTemplates.find((t) => t.id === c.emailTemplateId)?.name || "",
        smsTemplateName:
          smsTemplates.find((t) => t.id === c.smsTemplateId)?.name || "",
      })),
    [campaigns, customers.length, emailTemplates, smsTemplates],
  );

  const handleRowClick = (campaign: Campaign) => {
    setSelected(campaign);
    setDrawerOpen(true);
  };

  const handleRunCampaign = async (campaign: Campaign) => {
    try {
      setRunningCampaignId(campaign.id);
      const response = await runCampaignApi(campaign.id);
      const result = response?.data?.result;
      const message = response?.message || `Campaign "${campaign.name}" completed.`;

      if (result === "failed") {
        toast.error(message);
        return;
      }

      if (result === "partial") {
        toast.warning(message);
        return;
      }

      toast.success(message);
    } catch (err) {
      toast.error(formatApiErrors(err));
    } finally {
      setRunningCampaignId(null);
    }
  };

  return (
    <div className="p-4 lg:p-6">
      <div className="flex flex-col w-full h-full">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base lg:text-lg font-semibold text-gray-900">
              Campaigns
            </h2>
            <p className="text-xs lg:text-sm text-gray-500">
              Combine templates with selected customers to run targeted campaigns.
            </p>
          </div>
          <Button
            onClick={() => navigate("/marketing/campaigns/create")}
            className="bg-[#885ABB] text-white hover:bg-[#6f47a2]"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            New Campaign
          </Button>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {campaignsLoading ? (
            <TemplateListSkeleton />
          ) : enrichedCampaigns.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              No campaigns yet. Click{" "}
              <span className="font-semibold text-[#885ABB]">New Campaign</span>{" "}
              to create your first one.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {enrichedCampaigns.map((campaign) => {
                const hasEmail = Boolean(campaign.emailTemplateId);
                const hasSms = Boolean(campaign.smsTemplateId);
                const running = runningCampaignId === campaign.id;
                return (
                  <div
                    key={campaign.id}
                    className="px-4 lg:px-5 py-3 lg:py-3.5 hover:bg-purple-50/40 transition flex items-center justify-between gap-3"
                  >
                    <button
                      type="button"
                      onClick={() => handleRowClick(campaign)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {campaign.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {campaign.audienceLabel}
                      </p>
                    </button>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 text-[11px] text-gray-500">
                        {hasEmail && (
                          <span className="px-2 py-0.5 rounded-full bg-purple-50 text-[#885ABB] font-semibold">
                            Email
                          </span>
                        )}
                        {hasSms && (
                          <span className="px-2 py-0.5 rounded-full bg-purple-50 text-[#885ABB] font-semibold">
                            SMS
                          </span>
                        )}
                        {!hasEmail && !hasSms && (
                          <span className="text-red-500 font-semibold">
                            No channels
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-semibold text-[#885ABB] whitespace-nowrap">
                        {campaign.creditsUsed.toFixed(2)}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => void handleRunCampaign(campaign)}
                        disabled={running}
                        title="Send campaign"
                        aria-label={`Send ${campaign.name}`}
                        className="bg-[#885ABB] text-white hover:bg-[#6f47a2]"
                      >
                        {running ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Send className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <CampaignDetailsDrawer
        isOpen={drawerOpen}
        campaign={selected}
        emailTemplateName={
          selected
            ? emailTemplates.find((t) => t.id === selected.emailTemplateId)?.name
            : undefined
        }
        smsTemplateName={
          selected
            ? smsTemplates.find((t) => t.id === selected.smsTemplateId)?.name
            : undefined
        }
        customerGroupName={
          selected
            ? selected.useAllCustomers
              ? `All customers (${customers.length})`
              : `${selected.selectedCustomerIds.length} selected customer${selected.selectedCustomerIds.length === 1 ? "" : "s"}`
            : undefined
        }
        onClose={() => setDrawerOpen(false)}
        onEdit={(id) => navigate(`/marketing/campaigns/${id}/edit`)}
        onDelete={async (id) => {
          try {
            await removeCampaign(id);
            toast.success("Campaign deleted.");
            setSelected(null);
          } catch (err) {
            toast.error(formatApiErrors(err));
            throw err;
          }
        }}
      />
    </div>
  );
};

export default CampaignsListPage;

